"use client";

import { useEffect } from "react";
import Link from "next/link";

import { StatusPage } from "@/components/StatusPage";
import { Button, buttonVariants } from "@/components/ui/button";
import { useT } from "@/i18n/react";

export type ErrorViewProps = {
    error: Error & { digest?: string };
    /** Re-renders the segment that failed. */
    reset: () => void;
};

/**
 * Body of every `error.tsx`. The message and stack stay in the console (and the
 * server log, keyed by the digest): they can name internals the visitor must
 * not see. The digest is shown as a reference to quote to support.
 */
export function ErrorView({ error, reset }: ErrorViewProps) {
    const t = useT();
    useEffect(() => {
        console.error(error);
    }, [error]);

    return (
        <StatusPage
            title={t("errors.failure.title")}
            actions={
                <>
                    <Button onClick={reset}>{t("errors.tryAgain")}</Button>
                    <Link href="/dashboard" className={buttonVariants({ variant: "outline" })}>
                        {t("errors.backToDashboard")}
                    </Link>
                </>
            }
        >
            <p>{t("errors.failure.detail")}</p>
            {error.digest ? <p className="mt-2 text-sm">{t("errors.reference", { digest: error.digest })}</p> : null}
        </StatusPage>
    );
}
